import React, { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { motion } from "framer-motion";
import {
  FaHome,
  FaInfoCircle,
  FaServicestack,
  FaEnvelope,
  FaBars,
  FaTimes,
  FaUsers,
  FaClipboardList,
  FaEye,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import LogoutButton from "./LogoutButton";
import LogoSVG from "../assets/images/logo.png";
import { useAuth } from "../context/AuthContext";
import {
  BookCheck,
  Check,
  CreditCard,
  Crown,
  DollarSign,
  FileCheck,
  Gauge,
  IndianRupee,
  ShoppingCart,
  Tag,
  X,
  Plus,
  Users,
  MessageSquare,
  HomeIcon,
  Zap,
} from "lucide-react";
import { Button, message } from "antd";
import { postData } from "../api/apiService";
import { toast } from "react-toastify";
import PaymentDrawer from "./PaymentDrawer";
import { LoadingOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { useSubscriptionStatus } from "../hooks/useSubscriptionStatus";
import { isPartnerSsoSession } from "../utils/embedFlow";
import { PRICING } from "../api/endpointMap";
import Logo from "../assets/images/NabsLogoOld.png";
import NoticeAILogo from "../assets/images/LoadingLogo.png";
import SubscriptionDropdown from "./SubscriptionDropdown";
import { useFeatureEnabled } from "../hooks/usePlanFeatures";
import { FEATURE_KEYS } from "../utils/featureKeys";
import useIsEmbedMode from "../hooks/useIsEmbedMode";

// Shared predicate for the navbar action buttons. Hides them in
// partner-SSO sessions, before the user is known, and for admin /
// partner-admin roles (they have their own admin chrome). Returns
// false if `status` hasn't loaded yet so we don't flash buttons
// before the subscription state is known.
const _showNavbarPlanActions = (user, status) => {
  if (isPartnerSsoSession()) return false;
  if (!user) return false;
  if (user.role && ["super_admin", "partner_admin", "partner_admin_user"].includes(user.role)) {
    return false;
  }
  return !!status;
};

// Shared visual treatment for the outlined nav buttons. White background
// with a navy border + navy text matches the rest of the product chrome
// (Logout button uses the same navy as a filled primary; these stay
// outlined so the cluster reads as primary → outlined → outlined →
// filled left-to-right). On hover the background tints toward navy-50
// so the button feels active without changing colour identity.
const _NAVY = "#0a1e54";
const _NAVY_HOVER_BG = "#eef1fb"; // navy-tinted off-white
const _navOutlinedStyle = {
  height: 32,
  borderColor: _NAVY,
  color: _NAVY,
  background: "#ffffff",
  fontWeight: 500,
  letterSpacing: "0.01em",
  // Slightly thicker border so the outline reads as deliberate vs the
  // hairline 1px borders AntD uses on inputs / dropdowns elsewhere.
  borderWidth: 1.5,
};

const _onNavBtnEnter = (e) => {
  e.currentTarget.style.background = _NAVY_HOVER_BG;
};
const _onNavBtnLeave = (e) => {
  e.currentTarget.style.background = "#ffffff";
};

// Standalone "Upgrade" button. Outlined navy to match the product chrome.
// A subtle crown icon hints at the premium/upgrade intent without
// shouting; the Usage button right next to it uses a gauge so the two
// are visually distinct at a glance.
const NavbarUpgradeButton = () => {
  const { user } = useAuth();
  const { status } = useSubscriptionStatus();
  const navigate = useNavigate();
  if (!_showNavbarPlanActions(user, status)) return null;
  return (
    <Button
      className="exempt-from-readonly"
      style={_navOutlinedStyle}
      onMouseEnter={_onNavBtnEnter}
      onMouseLeave={_onNavBtnLeave}
      icon={<Crown size={14} strokeWidth={2.25} />}
      onClick={() => navigate("/shop")}
      aria-label="Upgrade plan"
    >
      Upgrade
    </Button>
  );
};

// Standalone "Usage" button. Same outlined treatment as Upgrade so the
// pair reads as a uniform set; the gauge icon differentiates intent.
const NavbarUsageButton = () => {
  const { user } = useAuth();
  const { status } = useSubscriptionStatus();
  const navigate = useNavigate();
  if (!_showNavbarPlanActions(user, status)) return null;
  return (
    <Button
      className="exempt-from-readonly"
      style={_navOutlinedStyle}
      onMouseEnter={_onNavBtnEnter}
      onMouseLeave={_onNavBtnLeave}
      icon={<Gauge size={14} strokeWidth={2.25} />}
      onClick={() => navigate("/usage")}
      aria-label="Usage"
    >
      Usage
    </Button>
  );
};

export const Navbar = () => {
  // Live subscription state for the read-only badge — module-cached hook,
  // shared with the Upgrade/Usage buttons, so no extra request.
  const { status } = useSubscriptionStatus();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { isReadOnly, isFirstPurchase, user } = useAuth();
  // Users-page entry visibility (USER_LEVEL_CLIENT_ASSIGNMENT.md §7.5).
  // Label was renamed Team → Users on 2026-05-06 per
  // USER_ASSIGN_CHANGES_PLAN.md §1.4 — URL stays "/team" so existing
  // bookmarks / impersonation links / external references keep working;
  // only the visible label changed. Visible to tenant_admin +
  // super_admin; hidden from regular users and partner_admin (they have
  // their own /partner-admin surface). Single-user-org admins still see
  // the link — they need it to invite user #2.
  const isTenantAdmin =
    user?.role === "tenant_admin" || user?.role === "super_admin";
  // Phase 4 — also hide the Users entry when the plan disables
  // MULTI_USER_SUPPORT. Combined with the role check above, the link
  // shows only when: caller is an admin AND the plan grants multi-user.
  const multiUserEnabled = useFeatureEnabled(FEATURE_KEYS.MULTI_USER_SUPPORT);
  // Hide Users entry in embed iframe (partner simulator) for ALL roles —
  // team management is the partner system's responsibility there, not
  // NoticeAI's. `useIsEmbedMode` covers both the layout-mount and the
  // partner-SSO-session paths (defence in depth).
  const isEmbed = useIsEmbedMode();
  const showUsersLink = isTenantAdmin && multiUserEnabled && !isEmbed;
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedPortals, setSelectedPortals] = useState([
    "GST",
    "Income Tax",
    "TDS",
  ]);
  const [promoCode, setPromoCode] = useState("");
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promoApplied, setPromoApplied] = useState({
    applied: false,
    error: null,
    message: null,
  });

  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);

  // New state for addons
  const [showAddons, setShowAddons] = useState(false);
  const [additionalClients, setAdditionalClients] = useState(0);
  const [additionalResponses, setAdditionalResponses] = useState(0);

  const pathname = window.location.pathname;

  const fetchPricingDetails = async (
    portals,
    promo = "",
    addClients = 0,
    addResponses = 0
  ) => {
    setLoading(true);
    const payload = {
      selected_portals: portals,
      promo_code: promo,
      additional_clients: addClients,
      additional_responses: addResponses,
    };
    try {
      setLoading(true);
      const response = await postData(
        PRICING.GET_DETAILS,
        payload
      );

      setTimeout(() => {
        setPricingData(response?.result);
        if (
          response?.result?.promo_code?.is_promo_code_valid &&
          response?.result?.promo_code?.promo_code
        ) {
          setPromoApplied({
            applied: true,
            error: false,
            message: response?.result?.promo_code?.status,
          });
        } else if (
          response?.result?.promo_code?.promo_code &&
          !response?.result?.promo_code?.is_promo_code_valid
        ) {
          setPromoApplied({
            applied: true,
            error: true,
            message: response?.result?.promo_code?.status,
          });
        } else {
          setPromoApplied({
            applied: false,
            error: null,
            message: null,
          });
        }

        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error fetching pricing:", error);
      toast.error("Failed to fetch pricing details");
      setLoading(false);
    }
  };

  const handlePortalToggle = (portal) => {
    setSelectedPortals((prev) =>
      prev.includes(portal)
        ? prev.filter((p) => p !== portal)
        : [...prev, portal]
    );
  };

  const applyPromoCode = () => {
    if (promoCode.trim()) {
      fetchPricingDetails(
        selectedPortals,
        promoCode,
        additionalClients,
        additionalResponses
      );
    }
  };

  const removePromoCode = () => {
    setPromoCode("");
    setPromoApplied({
      applied: false,
      error: null,
      message: null,
    });
    fetchPricingDetails(
      selectedPortals,
      "",
      additionalClients,
      additionalResponses
    );
  };

  const handleAddonUpdate = () => {
    fetchPricingDetails(
      selectedPortals,
      promoCode,
      additionalClients,
      additionalResponses
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  // const debouncedUpdatePricing = useMemo(
  //   () =>
  //     debounce((clients, responses) => {
  //       fetchPricingDetails(selectedPortals, promoCode, clients, responses);
  //     }, 500),
  //   [selectedPortals, promoCode]
  // );

  // useEffect(() => {
  //   if (showPricingModal) {
  //     debouncedUpdatePricing(additionalClients, additionalResponses);
  //   }
  // }, [additionalClients, additionalResponses]);

  const PricingCard = ({
    title,
    originalPrice,
    finalPrice,
    isSelected,
    onToggle,
    clientAddition,
    responseGenration,
    originalClientAddition,
    originalResponseGenration,
    icon: IconComponent = DollarSign,
    loading = false,
  }) => {
    return (
      <div
        className={`relative p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer w-full  ${
          isSelected
            ? "border-blue-500 bg-blue-50 shadow-lg transform scale-102"
            : "border-gray-200 hover:border-gray-300 hover:shadow-md"
        }`}
        onClick={onToggle}
      >
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
            isSelected ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600"
          }`}
        >
          <IconComponent size={20} />
        </div>

        {isSelected && (
          <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1.5 shadow-lg">
            <Check size={14} />
          </div>
        )}

        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-4">{title}</h3>
        </div>
      </div>
    );
  };

  // useEffect(() => {
  //   if (showPricingModal) {
  //     fetchPricingDetails(
  //       selectedPortals,
  //       promoCode,
  //       additionalClients,
  //       additionalResponses
  //     );
  //   }
  // }, [selectedPortals, showPricingModal]);

  return (
    <>
      <nav className="bg-white shadow-md fixed w-full top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-4 exempt-from-readonly">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: window.innerWidth <= 768 ? "20px" : "36px",
                fontWeight: 700,
              }}
            >
              <img src={Logo} className="nabslogo" draggable={false} />
            </motion.h1>

            {/* A status badge, not an action. Amber carries the right
                meaning: a warning about a restriction, not a primary action.

                Driven by TWO sources deliberately: the legacy localStorage
                read-only flag, and the LIVE subscription state — the same
                state machine the backend now enforces mutating actions with.
                A trial that expired a minute ago shows the badge on the next
                page load, without waiting for any nightly job. */}
            {(() => {
              const subState = status?.subscription_state;
              const subExpired =
                subState === "trial_expired" || subState === "expired";
              if (!isReadOnly && !subExpired) return null;
              const label = subExpired
                ? subState === "trial_expired"
                  ? "Trial ended · Read-only"
                  : "Subscription ended · Read-only"
                : "Read-only";
              return (
                <div
                  title="You can view everything, but actions are paused. Upgrade to restore them."
                  className="hidden md:inline-flex items-center gap-2 rounded-full border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 pl-2.5 pr-1 py-1 text-amber-800 shadow-sm"
                >
                  <FaEye className="text-amber-600 text-xs" />
                  <span className="text-xs font-semibold whitespace-nowrap">
                    {label}
                  </span>
                  {subExpired && (
                    <span
                      role="button"
                      onClick={(e) => {
                        // The badge lives inside the logo's <Link> — without
                        // this the click would ALSO navigate home.
                        e.preventDefault();
                        e.stopPropagation();
                        navigate("/shop");
                      }}
                      className="exempt-from-readonly cursor-pointer rounded-full bg-gradient-to-r from-blue-700 to-blue-500 px-2.5 py-0.5 text-[11px] font-semibold text-white shadow hover:from-blue-800 hover:to-blue-600 whitespace-nowrap"
                    >
                      Upgrade
                    </span>
                  )}
                </div>
              );
            })()}
          </Link>

          {pathname !== "/" && (
            <motion.h4
              className="text-4xl md:text-3xl font-bold text-white leading-tight  rounded-lg"
              initial={{ opacity: 0, y: -50, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.8,
                delay: 0.2,
                type: "spring",
                stiffness: 100,
                damping: 15,
              }}
            >
              <motion.span
                className="text-blue-800 drop-shadow-lg"
                transition={{ duration: 2, repeat: Infinity }}
              >
                <img src={NoticeAILogo} width={150} className="noticeAiLogo" />
              </motion.span>
            </motion.h4>
          )}

          {/* Right-side cluster — every action sits in one row at a
              uniform 32px height with a 5px gap between adjacent items.
              The `[&>*]:h-8` selector forces every direct child to the
              same height; `gap-[5px]` is the literal 5px the user asked
              for. The list is wrapped in a flex container instead of
              a `<ul>` with `space-x` because list-item children make
              equal-height harder to guarantee. */}
          <ul className="hidden md:flex items-center gap-1.25 ml-4 exempt-from-readonly [&>li]:flex [&>li]:items-center">
            {/* Hide Subscription status in embed mode (partner simulator
                iframe). The partner system surfaces subscription state on
                its own dashboard; surfacing it twice inside the embedded
                shell is noisy and the value is not actionable from there. */}
            {!isEmbed && (
              <li>
                <SubscriptionDropdown />
              </li>
            )}

            <li>
              <NavbarUpgradeButton />
            </li>

            <li>
              <NavbarUsageButton />
            </li>

            {showUsersLink && (
              <li>
                <Button
                  className="exempt-from-readonly"
                  style={_navOutlinedStyle}
                  onMouseEnter={_onNavBtnEnter}
                  onMouseLeave={_onNavBtnLeave}
                  icon={<Users size={14} strokeWidth={2.25} />}
                  onClick={() => navigate("/team")}
                  aria-label="Users"
                >
                  Users
                </Button>
              </li>
            )}

            {isFirstPurchase && (
              <li>
                <button
                  onClick={() => setShowPricingModal(true)}
                  className="group relative inline-flex items-center px-4 h-8 bg-linear-to-r from-blue-500 to-purple-600 text-white text-sm font-semibold rounded-md shadow hover:shadow-md transition-all duration-200 overflow-hidden"
                >
                  <ShoppingCart size={16} className="mr-1.5 relative z-10" />
                  <span className="relative z-10">Buy Now</span>
                </button>
              </li>
            )}

            <li>
              <LogoutButton />
            </li>
          </ul>

          <button
            className="md:hidden text-gray-700 focus:outline-none"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden bg-white shadow-md">
            <ul className="flex flex-col space-y-4 p-4">
              {showUsersLink && (
                <li>
                  <Link
                    to="/team"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-2 text-gray-700 hover:text-black"
                  >
                    <Users className="w-5 h-5 text-gray-500" />
                    <span className="font-medium">Users</span>
                  </Link>
                </li>
              )}
              <li>
                <LogoutButton />
              </li>
            </ul>
          </div>
        )}
      </nav>

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl relative shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 z-50 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold text-blue-900">
                    Choose Your Plan
                  </h2>
                  <p className="text-gray-600 mt-1">
                    Select the modules that best fit your needs
                  </p>
                </div>
                <button
                  onClick={() => setShowPricingModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} className="text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-8 py-6">
              {/* Features Section */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 text-blue-800">
                  What You Get
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg">
                    <Check className="text-blue-500" size={20} />
                    <span className="text-gray-700">
                      Automated Notice Fetching
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
                    <Check className="text-green-500" size={20} />
                    <span className="text-gray-700">
                      AI-Powered Response Drafting
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg">
                    <Check className="text-purple-500" size={20} />
                    <span className="text-gray-700">
                      Centralized Notice Dashboard
                    </span>
                  </div>
                </div>
              </div>

              {/* Pricing Cards */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-blue-800 mb-6">
                  Select Modules
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <PricingCard
                    title="GST"
                    originalPrice={pricingData?.gst_pack?.price || 30000}
                    finalPrice={pricingData?.gst_pack?.final_price || 28000}
                    originalClientAddition={pricingData?.client_addition || 200}
                    clientAddition={pricingData?.final_client_addition || 280}
                    originalResponseGenration={
                      pricingData?.response_generation || 200
                    }
                    responseGenration={
                      pricingData?.final_response_generation || 240
                    }
                    isSelected={selectedPortals.includes("GST")}
                    onToggle={() => handlePortalToggle("GST")}
                    icon={IndianRupee}
                    loading={loading}
                  />
                  <PricingCard
                    title="TDS"
                    originalPrice={pricingData?.tds_pack?.price || 30000}
                    finalPrice={pricingData?.tds_pack?.final_price || 27000}
                    originalClientAddition={pricingData?.client_addition || 200}
                    clientAddition={pricingData?.final_client_addition || 280}
                    originalResponseGenration={
                      pricingData?.response_generation || 200
                    }
                    responseGenration={
                      pricingData?.final_response_generation || 240
                    }
                    isSelected={selectedPortals.includes("TDS")}
                    onToggle={() => handlePortalToggle("TDS")}
                    icon={FileCheck}
                    loading={loading}
                  />
                  <PricingCard
                    title="Income Tax"
                    originalPrice={pricingData?.income_tax_pack?.price || 30000}
                    finalPrice={
                      pricingData?.income_tax_pack?.final_price || 25000
                    }
                    originalClientAddition={pricingData?.client_addition || 200}
                    clientAddition={pricingData?.final_client_addition || 280}
                    originalResponseGenration={
                      pricingData?.response_generation || 200
                    }
                    responseGenration={
                      pricingData?.final_response_generation || 240
                    }
                    isSelected={selectedPortals.includes("Income Tax")}
                    onToggle={() => handlePortalToggle("Income Tax")}
                    icon={BookCheck}
                    loading={loading}
                  />
                </div>
              </div>

              {/* Client & Response Details Section */}
              {pricingData && (
                <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <h3 className="text-lg font-semibold text-blue-800 mb-6 flex items-center">
                    <Users className="mr-2" size={20} />
                    Included Features
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Client Details */}
                    <div className="bg-white p-5 rounded-lg shadow-sm border">
                      <div className="flex items-center mb-3">
                        <Users className="text-blue-500 mr-2" size={18} />
                        <h4 className="font-semibold text-gray-800">
                          Client Access
                        </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Base Clients:</span>
                          <div className="flex items-center space-x-2">
                            <span className="line-through text-gray-400 text-sm">
                              {pricingData?.base_total
                                ?.total_base_client_addition || 600}
                            </span>
                            <span className="font-semibold text-green-600">
                              {pricingData?.total?.total_client_addition || 720}
                            </span>
                          </div>
                        </div>
                        {additionalClients > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Additional:</span>
                            <span className="font-semibold text-blue-600">
                              +{additionalClients}
                            </span>
                          </div>
                        )}
                        <div className="border-t pt-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-800">
                              Total Clients:
                            </span>
                            <span className="font-bold text-blue-600">
                              {(pricingData?.total?.total_client_addition ||
                                720) + additionalClients}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Response Details */}
                    <div className="bg-white p-5 rounded-lg shadow-sm border">
                      <div className="flex items-center mb-3">
                        <MessageSquare
                          className="text-green-500 mr-2"
                          size={18}
                        />
                        <h4 className="font-semibold text-gray-800">
                          Response Generation
                        </h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Base Responses:</span>
                          <div className="flex items-center space-x-2">
                            <span className="line-through text-gray-400 text-sm">
                              {pricingData?.base_total
                                ?.total_base_response_generation || 900}
                            </span>
                            <span className="font-semibold text-green-600">
                              {pricingData?.total?.total_response_generation ||
                                1080}
                            </span>
                          </div>
                        </div>
                        {additionalResponses > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Additional:</span>
                            <span className="font-semibold text-blue-600">
                              +{additionalResponses}
                            </span>
                          </div>
                        )}
                        <div className="border-t pt-2">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-800">
                              Total Responses:
                            </span>
                            <span className="font-bold text-green-600">
                              {(pricingData?.total?.total_response_generation ||
                                1080) + additionalResponses}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Add-ons Section */}
                  <div className="mt-6">
                    <button
                      onClick={() => setShowAddons(!showAddons)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Plus size={16} />
                      <span>{showAddons ? "Hide" : "Add"} Extra Features</span>
                    </button>

                    {showAddons && (
                      <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-800 mb-4">
                          Additional Features
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Additional Clients (
                              {formatPrice(
                                pricingData?.final_client_addon_cost || 30
                              )}{" "}
                              each)
                            </label>
                            <input
                              type="text"
                              value={additionalClients.toString()}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                                setAdditionalClients(
                                  Number(cleaned.replace(/^0+/, "") || 0)
                                );
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Additional Responses (
                              {formatPrice(
                                pricingData?.final_response_addon_cost || 10
                              )}{" "}
                              each)
                            </label>
                            <input
                              type="text"
                              value={additionalResponses.toString()}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(
                                  /\D/g,
                                  ""
                                );
                                setAdditionalResponses(
                                  Number(cleaned.replace(/^0+/, "") || 0)
                                );
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                              placeholder="0"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Promo Code Section */}
              {pricingData?.promo_codes_enabled && (
                <div className="mb-8 p-6 bg-gray-50 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Tag className="mr-2" size={20} />
                    Have a Promo Code?
                  </h3>

                  {promoApplied?.applied && !promoApplied?.error ? (
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <Check size={16} className="text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-green-800">
                            Promo code "{promoCode}" applied!
                          </div>
                          <div className="text-sm text-green-600">
                            {promoApplied.message}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={removePromoCode}
                        className="px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2"
                      >
                        <X size={16} />
                        <span>Remove</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex space-x-3">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          placeholder="Enter promo code"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                        <button
                          onClick={applyPromoCode}
                          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                          disabled={!promoCode.trim() || loading}
                        >
                          {loading ? <LoadingOutlined /> : "Apply"}
                        </button>
                      </div>
                      {promoApplied?.applied && promoApplied?.error && (
                        <div className="text-sm text-red-600 flex items-center space-x-2">
                          <X size={16} />
                          <span>{promoApplied.message}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Enhanced Order Summary */}
              <div className="mb-8 p-6 bg-white border-2 border-gray-200 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Order Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Base Total</span>
                    <div className="flex items-center space-x-2">
                      <span className="line-through text-gray-400 text-sm">
                        {formatPrice(
                          pricingData?.base_total?.total_base_price || 90000
                        )}
                      </span>
                      <span className="font-semibold">
                        {formatPrice(
                          pricingData?.total?.subtotal_price || 90000
                        )}
                      </span>
                    </div>
                  </div>

                  {(additionalClients > 0 || additionalResponses > 0) && (
                    <>
                      {additionalClients > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">
                            Additional Clients ({additionalClients})
                          </span>
                          <span className="font-semibold">
                            {formatPrice(
                              pricingData?.total?.additional_clients_amount || 0
                            )}
                          </span>
                        </div>
                      )}
                      {additionalResponses > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">
                            Additional Responses ({additionalResponses})
                          </span>
                          <span className="font-semibold">
                            {formatPrice(
                              pricingData?.total?.additional_responses_amount ||
                                0
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center font-semibold border-t pt-2">
                        <span className="text-gray-700">
                          Subtotal (with addons)
                        </span>
                        <span>
                          {formatPrice(
                            pricingData?.total?.subtotal_price +
                              (pricingData?.total?.additional_clients_amount ||
                                0) +
                              (pricingData?.total
                                ?.additional_responses_amount || 0)
                          )}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">System Discount</span>
                    <span className="text-green-600 font-semibold">
                      -{formatPrice(pricingData?.discount?.system_discount)}
                    </span>
                  </div>

                  {pricingData?.discount?.promo_code_discount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Promo Code Discount</span>
                      <span className="text-green-600 font-semibold">
                        -
                        {formatPrice(
                          pricingData?.discount?.promo_code_discount || 0
                        )}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Before Tax</span>
                    <span className="font-semibold">
                      {formatPrice(pricingData?.total?.total_price_without_tax)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      GST ({pricingData?.gst_tax?.percent || 18}%)
                    </span>
                    <span className="font-semibold">
                      {formatPrice(pricingData?.gst_tax?.amount)}
                    </span>
                  </div>

                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">
                        Final Total
                      </span>
                      <span className="text-2xl font-bold text-blue-600">
                        {formatPrice(pricingData?.total?.total_price)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purchase Button */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setIsPaymentDrawerOpen(true);
                  }}
                  className="group relative inline-flex items-center px-12 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 overflow-hidden"
                  disabled={selectedPortals.length === 0 || loading}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                  <CreditCard size={24} className="mr-3 relative z-10" />
                  <span className="relative z-10">
                    {loading ? "Processing..." : "Purchase Now"}
                  </span>
                  <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isPaymentDrawerOpen && (
        <PaymentDrawer
          isOpen={isPaymentDrawerOpen}
          setIsOpen={setIsPaymentDrawerOpen}
          selectedPortals={pricingData?.selected_portals}
          onDetailsSubmission={() => {
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
};
